import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CircleAlert, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { useDevice } from '@/store/device';
import { useSessions } from '@/store/sessions';
import { devicesApi, type DeviceListEntry } from '@/lib/api';
import { Breadcrumb } from '@/components/Breadcrumb';
import { formatDisplayName, useDeviceDisplayName } from '@/lib/displayName';

/**
 * Per-device shell. Sub-tabs:
 *   - /devices/:serial            → overview (reuses Dashboard; mirror docks here)
 *   - /devices/:serial/commands   → Commands
 *   - /devices/:serial/apps       → Apps (install/start/stop/clear/uninstall + export APK)
 *   - /devices/:serial/network    → Network
 *   - /devices/:serial/logs       → Logs
 *   - /devices/:serial/files      → Files
 *   - /devices/:serial/control    → Control (WS)
 *
 * 本机直连设备默认全用户可达，DeviceDetail 不再做 enter / 权限预检。
 */
export default function DeviceDetail() {
  const { serial: rawSerial } = useParams<{ serial: string }>();
  const serial = rawSerial ? decodeURIComponent(rawSerial) : undefined;
  const setSerial = useDevice((s) => s.setSerial);
  const setCurrentState = useDevice((s) => s.setCurrentState);
  const navigate = useNavigate();

  useEffect(() => {
    if (serial) setSerial(serial);
  }, [serial, setSerial]);

  // Auto-register the URL serial as an active sidebar session. This is
  // what lets deep-links / bookmarks ("/devices/<serial>") work even
  // when the user hasn't gone through the grid's sub-tab icon row —
  // they land on this page and immediately see the device in the
  // sidebar with its sub-tabs. We pass `force: true` so a bookmark to
  // a 4th device evicts the oldest open session rather than getting
  // rejected (the URL is authoritative; refusing to add would leave
  // the page orphaned with no sidebar entry).
  const addSession = useSessions((s) => s.add);
  useEffect(() => {
    if (serial) addSession(serial, { force: true });
  }, [serial, addSession]);

  // Reset connection state when the serial changes so the previous
  // device's `device` (online) flag doesn't leak into the new dashboard
  // for the half-second before the GET below resolves.
  useEffect(() => {
    setCurrentState('unknown');
  }, [serial, setCurrentState]);

  const { data, error, isLoading } = useQuery<{ device: DeviceListEntry }, Error & { status?: number }>({
    queryKey: ['device', serial],
    queryFn: () => devicesApi.get(serial!),
    enabled: !!serial,
    retry: (count, err) => {
      const e = err as Error & { status?: number };
      if (e.status === 404) return false;
      return count < 2;
    },
    refetchInterval: 15_000,
  });

  const displayLabel = useDeviceDisplayName(serial ?? null) || serial;

  // Mirror the device's adb state into the global store so legacy
  // descendants (Dashboard, Mirror, Logcat …) that still read from
  // `useDevice()` see the same online flag as the grid card. Without
  // this, the grid's card shows 🟢 在线 (correct, from /api/devices)
  // but the dashboard underneath the detail page shows the offline
  // banner (stale `currentState=unknown` because nobody was writing it
  // after we removed the top-bar DevicePicker).
  useEffect(() => {
    if (data?.device) {
      const s = data.device.state;
      setCurrentState(
        s === 'device' || s === 'offline' || s === 'unauthorized' ? s : 'unknown'
      );
    }
  }, [data?.device, setCurrentState]);

  // Compute display name + breadcrumb items at the top so the hooks are
  // unconditional — any conditional hook below the early returns
  // (e.g. moving `useMemo` into JSX) trips React's hook-order check and
  // the entire `<Outlet />` subtree blanks out.
  const device = data?.device;
  const displayName =
    device?.alias || device?.meta?.model || device?.model || device?.product || serial || '';
  const breadcrumbItems = useMemo(
    () => [
      { label: '设备', to: '/devices' },
      { label: <span className="truncate font-medium">{displayName}</span> },
    ],
    [displayName]
  );

  if (!serial) {
    return (
      <div className="p-6 text-sm text-ink-muted">
        <p>未指定设备序列号。</p>
        <button className="btn mt-2" onClick={() => navigate('/devices')}>
          返回设备列表
        </button>
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6 text-xs text-ink-muted">加载设备信息…</div>;
  }

  if (error) {
    const notFound = error.status === 404;
    return (
      <div className="p-6 text-sm">
        <div className="card max-w-md p-4">
          <div className="mb-2 flex items-center gap-2 font-medium text-amber-600">
            <CircleAlert className="h-4 w-4" />
            {notFound ? '设备未注册' : '加载设备失败'}
          </div>
          <p className="text-xs text-ink-muted">
            {notFound ? (
              <>
                注册表里找不到 <span className="font-mono">{displayLabel}</span>
                ，可能已经被移除或还没被 adb 识别。
              </>
            ) : (
              <span className="font-mono">{error.message}</span>
            )}
          </p>
          <button className="btn mt-3" onClick={() => navigate('/devices')}>
            返回设备列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-line bg-bg-card px-4 py-2">
        {/*
          Hardcoded prominent back button. The breadcrumb's "设备" link is
          easy to miss when you're new to the SPA — users reported being
          stuck on the detail page. An always-visible ← button next to
          the breadcrumb solves that without taking much room.
        */}
        <button
          className="btn-primary text-xs"
          onClick={() => navigate('/devices')}
          title="返回设备列表 (ESC)"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>设备列表</span>
        </button>
        <Breadcrumb items={breadcrumbItems} />
        <div className="hidden min-w-0 font-mono text-[11px] text-ink-dim sm:block">{serial}</div>
        {/*
          Per-device sub-nav now lives in the global left Sidebar (Phase
          2.2). Keeping the breadcrumb + serial in this header gives
          the user the same "where am I" context the in-page tabs used
          to provide.
        */}
      </header>
      {device && device.state !== 'device' && (
        <OfflineBanner device={device} serial={serial} />
      )}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * Strip across the top of the detail content when adb can see the device
 * but it's not actually in `device` state — most commonly `offline`
 * (wireless link dropped) or `unauthorized` (USB plug, no debug consent
 * yet). The dashboard underneath will then naturally show "loading" tiles
 * until adb reconnects.
 *
 * Behaviour:
 *   - USB / agent kinds → just explain how to resolve (replug, authorise).
 *   - Wireless kind → show endpoint + "立即重连" button. The first time
 *     this banner mounts for an offline wireless device we also fire a
 *     reconnect automatically; the watchdog would otherwise wait up to
 *     30 s. The button stays for retries.
 */
function OfflineBanner({
  device,
  serial,
}: {
  device: DeviceListEntry;
  serial: string;
}): JSX.Element {
  const qc = useQueryClient();
  const isWireless = device.kind === 'wireless';
  const reconnect = useMutation({
    mutationFn: () => devicesApi.reconnect(serial),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['device', serial] });
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });
  // Fire one auto-reconnect on mount when applicable. Ref-guarded so
  // remount during react-query refetch doesn't trigger a second attempt.
  const autoTried = useRef(false);
  const [autoState, setAutoState] = useState<'idle' | 'tried'>('idle');
  useEffect(() => {
    if (autoTried.current) return;
    if (!isWireless) return;
    if (device.state === 'device') return;
    autoTried.current = true;
    setAutoState('tried');
    reconnect.mutate();
    // mutate is stable from react-query; deps reflect intent only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWireless, device.state]);

  const deviceLabel = formatDisplayName(device, serial);
  const [headline, hint] =
    device.state === 'offline'
      ? isWireless
        ? ['📡 无线设备掉线 · offline', `endpoint ${device.endpoint ? `${device.endpoint.host}:${device.endpoint.port}` : '未记录'} · watchdog 每 30s 会自动重连，也可手动触发`]
        : ['📡 设备掉线 · offline', 'USB 请重插数据线，或在终端执行 `adb kill-server && adb start-server`。']
      : device.state === 'unauthorized'
        ? ['🔒 USB 调试未授权 · unauthorized', '在手机弹窗里勾选"始终允许此电脑"，或重新拔插数据线再次授权。']
        : [`⚠️ 设备状态：${device.state}`, `${deviceLabel} adb 状态不是 device，部分功能不可用。`];

  const reconnectError = reconnect.isError
    ? (reconnect.error as Error).message
    : null;

  return (
    <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-amber-700 dark:text-amber-300">{headline}</div>
          <div className="mt-0.5 text-amber-700/80 dark:text-amber-200/70">{hint}</div>
          {reconnect.isSuccess && (
            <div className="mt-0.5 text-emerald-700 dark:text-emerald-400">
              ✓ 已发起 adb connect — 设备应在数秒内上线
            </div>
          )}
          {reconnectError && (
            <div className="mt-0.5 break-all text-rose-700 dark:text-rose-400">
              重连失败：{reconnectError}
            </div>
          )}
          {autoState === 'tried' && reconnect.isPending && (
            <div className="mt-0.5 text-amber-700/70 dark:text-amber-200/60">
              正在尝试自动重连…
            </div>
          )}
        </div>
        {isWireless && (
          <button
            className="btn h-7 px-2 text-[11px]"
            onClick={() => reconnect.mutate()}
            disabled={reconnect.isPending}
            title="对此 endpoint 立刻执行 adb connect"
          >
            <RefreshCw
              className={clsx('h-3 w-3', reconnect.isPending && 'animate-spin')}
            />
            {reconnect.isPending ? '重连中…' : '立即重连'}
          </button>
        )}
      </div>
    </div>
  );
}

